

import { Component, Mixins, ProvideReactive, Watch } from "vue-property-decorator";
import { Getter, Mutation } from "vuex-class";
import { TLang } from "@/models/modules/lang/interface";
import { LANG_MODULE_NAME } from "@/store/modules/lang";
import { NOTIFICATION_MODULE_NAME } from "@/store/modules/notification";
import { LOADING_MODULE_NAME } from "@/store/modules/loading";
import { INotificationItem } from "@/store/modules/notification/interface";
import MainLayout from "@/layout/MainLayout.vue";
import AuthorizationLayout from "@/layout/AuthorizationLayout.vue";
import CompanyAccountsLayout from "@/layout/CompanyAccountsLayout.vue";
import ToastList from "@/components/Notification/ToastList.vue";
import { APP_MODULE_NAME } from "@/store/modules/app";
import EventEmitter from "@/modules/Event/EventEmitter";
import { EventEmitterName } from "@/modules/Event/interface";
import { $getLastNotification, $putReadNotificationList } from "@/api/notification";
import NotificationCore from "@/modules/NotificationCore/NotificationCore";
import CustomerActivityController from "@/feature/Auth/mixins/CustomerActivityController";
import AppVersionController from '@/feature/AppVersion/mixins/AppVersionController';
import IBAlertContainer from "@/components/IBAlert/IBAlertFixed/IBAlertContainer.vue";
import IBNotificationRead from "./components/Notification/IBNotificationRead";

@Component({
	components: {
		IBAlertContainer,
		MainLayout,
		AuthorizationLayout,
		CompanyAccountsLayout,
		ToastList
	}
})
export default class App extends Mixins(
	CustomerActivityController,
	AppVersionController,
	IBNotificationRead
) {

	@Getter('importantToasts', { namespace: NOTIFICATION_MODULE_NAME })
	readonly importantToasts!: INotificationItem[]

	@ProvideReactive('textFieldShowError')
	valid: boolean = true

	@Getter("toasts", { namespace: NOTIFICATION_MODULE_NAME })
	toasts!: INotificationItem[];

	@Getter("lang", { namespace: LANG_MODULE_NAME })
	lang!: TLang;

	@Getter('isActive', { namespace: LOADING_MODULE_NAME })
	loadingIsActive!: boolean;

	@Getter('appKey', { namespace: APP_MODULE_NAME })
	appKey!: number

	@Mutation('increaseAppKey', { namespace: APP_MODULE_NAME })
	increaseAppKey() {}

	@Mutation('increaseContentKey', { namespace: APP_MODULE_NAME })
	increaseContentKey() {}

	@Mutation('setToasts', { namespace: NOTIFICATION_MODULE_NAME })
	setToasts(_toasts: INotificationItem[]) {}

	@Mutation('deleteToast', { namespace: NOTIFICATION_MODULE_NAME })
	deleteToast(_toastId: number) {}

	@Mutation('clearToasts', { namespace: NOTIFICATION_MODULE_NAME })
	clearToasts() {}

	public isSpinner: boolean = false

	public lastNotificationId: number = 0

	public showedNotificationIds: number[] = []

	public notificationIsLoading: boolean = false

	public notificationModal = {
		isActive: false,
		btnGroup: [
			{
				name: this.$t("login.passwordReset.close"),
				class: "btn-primary"
			}
		],
		title: "",
		text: ""
	};

	created() {
		this.notificationModal.title = this.$t("errors.errorTitle") as string;

		this.$route.name && this.loadLastNotification();
		this.initAppEventListeners();
	}

	initAppEventListeners() {
		EventEmitter.addListener(
			EventEmitterName.COMPANY_CHANGE,
			() => {
				this.increaseContentKey();
				this.setToasts([]);
				this.loadLastNotification();
			}
		);

		EventEmitter.addListener(EventEmitterName.LOAD_NOTIFICATION, () =>
			this.loadLastNotification());

		EventEmitter.addListener(EventEmitterName.LOGOUT, () =>
			this.clearToasts());
	}

	loadLastNotification() {
		if (this.notificationIsLoading || !this.isAuth) return;

		this.notificationIsLoading = true;

		$getLastNotification({ disableLoader: true })
			.then(async ([ notification ]) => {
				if (!notification || this.isShowedNotification(notification)) return;

				this.deleteToast(this.lastNotificationId);
				this.showedNotificationIds.push(notification.id);

				this.lastNotificationId = await NotificationCore.addToastWithData({
					title: notification.title || this.$t("home.notification.title") as string,
					text: notification.text || this.$t("home.notification.text") as string,
					status: "warning",
					link: this.$t("home.notification.openText") as string,
					linkHandler: (v) => {
						this.lastNotificationId = 0;

						this.deleteToast(v.id);

						$putReadNotificationList([ notification.id ])
							.then(() => EventEmitter.emit(EventEmitterName.OPEN_NOTIFICATION));
					},
					action: (value: INotificationItem) => {
						this.addReadNotificationIds([notification.id]);
						this.deleteToast(value.id);
					}
				});
			})
			.finally(() => this.notificationIsLoading = false);
	}

	isShowedNotification(notification: any): boolean {
		return this.showedNotificationIds.includes(notification.id);
	}

	toggleNotificationModal() {
		this.notificationModal.isActive = !this.notificationModal.isActive;
	}

	get layout() {
		return (this.$route.meta!.layout || "main") + "-layout";
	}

	@Watch("lang")
	onLangChanged() {
		this.increaseAppKey();
	}

	@Watch("toasts")
	onToastsChanged(v: INotificationItem[]) {
		try {
			const toast: INotificationItem | undefined = v && v.slice(-1)[0];

			if (toast && toast.showModal) {
				this.notificationModal.text = toast.text;

				this.notificationModal.isActive = true;
			}
		} catch (err) {
			console.warn("App -> Watch -> onToastsChanged", err);
		}
	}

}

